/**
 * ConnectionsPage Component
 * Manages bank connections - view, add, reconnect, and delete connections
 */

import { useCallback, useState } from 'react';
import { Card, CardHeader, Button, Alert, Spinner } from '@/components/ui';
import { PlaidLinkButton, ConnectionCard } from '@/components/plaid';
import ConnectionDetailsModal from '@/components/plaid/ConnectionDetailsModal';
import {
  useConnections,
  useDeleteConnection,
  useSyncConnection,
  useOnConnectionCreated,
} from '@/hooks/useConnections';
import { useSyncStore } from '@/store/syncStore';
import type { ExchangeTokenResponse, Connection } from '@/types';

/**
 * Empty state component when no connections exist
 */
function EmptyState({ onAddConnection }: { onAddConnection: () => void }) {
  return (
    <Card className="text-center py-12">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-100">
        <svg
          className="h-8 w-8 text-primary-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z"
          />
        </svg>
      </div>
      <h3 className="mt-4 text-lg font-semibold text-gray-900">No accounts connected</h3>
      <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
        Connect your bank accounts, credit cards, or investment accounts to start tracking your finances and visualizing your money flow.
      </p>
      {/* WP-CONN-1: Single button for all account types - backend handles products */}
      <div className="mt-6">
        <PlaidLinkButton
          variant="primary"
          size="lg"
          onSuccess={() => onAddConnection()}
        >
          Connect Account
        </PlaidLinkButton>
      </div>
    </Card>
  );
}

/**
 * Success banner after adding a connection
 */
function SuccessBanner({
  connectionName,
  onDismiss,
}: {
  connectionName: string;
  onDismiss: () => void;
}) {
  return (
    <Alert variant="success" onDismiss={onDismiss} className="mb-6">
      <span className="font-medium">{connectionName}</span> has been connected successfully! Your
      accounts and transactions will start syncing shortly.
    </Alert>
  );
}

/**
 * ConnectionsPage - Main page for managing bank connections
 */
export default function ConnectionsPage() {
  const { data: connections, isLoading, error, refetch } = useConnections();
  const deleteConnection = useDeleteConnection();
  const syncConnection = useSyncConnection();
  const onConnectionCreated = useOnConnectionCreated();

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [detailsConnectionId, setDetailsConnectionId] = useState<string | null>(null);

  // WP-RT-2: Use global sync state from SSE events
  const { isSyncing, setSyncing } = useSyncStore();

  /**
   * Handle successful new connection
   */
  const handleConnectionSuccess = useCallback(
    (response: ExchangeTokenResponse) => {
      onConnectionCreated(response);
      setSuccessMessage('Your bank');
      // Clear success message after 10 seconds
      setTimeout(() => setSuccessMessage(null), 10000);
    },
    [onConnectionCreated]
  );

  /**
   * Handle connection deletion
   */
  const handleDelete = useCallback(
    async (connectionId: string) => {
      setDeletingId(connectionId);
      try {
        await deleteConnection.mutateAsync(connectionId);
      } finally {
        setDeletingId(null);
      }
    },
    [deleteConnection]
  );

  /**
   * Handle connection sync
   * WP-RT-2: Uses global sync state - sync.completed SSE event will clear it
   */
  const handleSync = useCallback(
    async (connectionId: string) => {
      setSyncing(connectionId, true); // Mark as syncing globally
      try {
        await syncConnection.mutateAsync(connectionId);
        // Note: Don't clear sync state here!
        // sync.completed SSE event will clear it when backend finishes
      } catch {
        setSyncing(connectionId, false); // Only clear on error
      }
    },
    [syncConnection, setSyncing]
  );

  /**
   * Handle reconnection success
   */
  const handleReconnect = useCallback(() => {
    refetch();
    setSuccessMessage('Your bank');
    setTimeout(() => setSuccessMessage(null), 10000);
  }, [refetch]);

  /**
   * Handle view details click - opens ConnectionDetailsModal
   */
  const handleViewDetails = useCallback((connectionId: string) => {
    setDetailsConnectionId(connectionId);
  }, []);

  /**
   * Group connections by status for display
   */
  const groupedConnections = connections?.reduce(
    (acc, conn) => {
      if (conn.status === 'error' || conn.status === 'pending_reauth') {
        acc.needsAttention.push(conn);
      } else {
        acc.healthy.push(conn);
      }
      return acc;
    },
    { needsAttention: [] as Connection[], healthy: [] as Connection[] }
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <Alert variant="error" title="Failed to load connections">
          {error instanceof Error ? error.message : 'An unexpected error occurred.'}
          <div className="mt-4">
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Try Again
            </Button>
          </div>
        </Alert>
      </div>
    );
  }

  // Empty state
  if (!connections || connections.length === 0) {
    return (
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Connections</h1>
        <EmptyState onAddConnection={() => {}} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Connections</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your connected bank accounts, credit cards, and investment accounts.
          </p>
        </div>
        {/* WP-CONN-1: Single button for all account types - backend handles products */}
        <PlaidLinkButton variant="primary" onSuccess={handleConnectionSuccess}>
          Connect Account
        </PlaidLinkButton>
      </div>

      {/* Success banner */}
      {successMessage && (
        <SuccessBanner
          connectionName={successMessage}
          onDismiss={() => setSuccessMessage(null)}
        />
      )}

      {/* Connections requiring attention */}
      {groupedConnections && groupedConnections.needsAttention.length > 0 && (
        <div className="mb-8">
          <Card padding="none">
            <CardHeader
              title="Needs Attention"
              description="These connections require action to continue syncing."
              className="px-4 py-3 border-b border-gray-200"
            />
            <div className="divide-y divide-gray-200">
              {groupedConnections.needsAttention.map((connection, index) => (
                <div key={connection.id} className="p-4">
                  <ConnectionCard
                    connection={connection}
                    onDelete={handleDelete}
                    onSync={handleSync}
                    onReconnect={handleReconnect}
                    onViewDetails={handleViewDetails}
                    isDeleting={deletingId === connection.id}
                    isSyncing={isSyncing(connection.id)}
                    defaultExpanded={index === 0}
                  />
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Healthy connections */}
      {groupedConnections && groupedConnections.healthy.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Connected Institutions
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({groupedConnections.healthy.length})
            </span>
          </h2>
          <div className="space-y-4">
            {groupedConnections.healthy.map((connection, index) => (
              <ConnectionCard
                key={connection.id}
                connection={connection}
                onDelete={handleDelete}
                onSync={handleSync}
                onReconnect={handleReconnect}
                onViewDetails={handleViewDetails}
                isDeleting={deletingId === connection.id}
                isSyncing={isSyncing(connection.id)}
                defaultExpanded={index === 0}
              />
            ))}
          </div>
        </div>
      )}

      {/* Delete error */}
      {deleteConnection.isError && (
        <Alert variant="error" className="mt-4" onDismiss={() => deleteConnection.reset()}>
          Failed to remove connection. Please try again.
        </Alert>
      )}

      {/* Sync error */}
      {syncConnection.isError && (
        <Alert variant="error" className="mt-4" onDismiss={() => syncConnection.reset()}>
          Failed to sync connection. Please try again later.
        </Alert>
      )}

      {/* Connection Details Modal - WP-FRONTEND-CONN-1 */}
      <ConnectionDetailsModal
        isOpen={detailsConnectionId !== null}
        onClose={() => setDetailsConnectionId(null)}
        connectionId={detailsConnectionId}
        onReconnect={handleReconnect}
      />
    </div>
  );
}
